import { useEffect, useState } from 'react';
import { formatPercent, formatRial } from '@kia/shared';
import { api } from '../api/client';
import { Card, Chip, Kpi } from '../ui';

/**
 * سنجش ضایعات مواد (اطلاعاتی) — the MEASURED material-usage variance for the month (accuracy audit #5).
 * Read-only and DELIBERATELY separate from the scrap-cover figure: per D6, yield loss is NOT in unit COGS —
 * the ~5% unbooked scrap income already absorbs it — so this only INFORMS ("is the BOM realistic? is a batch
 * spoiling?") and must never be added to cost or subtracted from the cover.
 */
interface YieldReport {
  ym: string;
  basis: 'measured' | 'insufficient' | 'no_production_data';
  sampleWos: number;
  matchedNominalValue: number;
  matchedActualValue: number;
  varianceValue: number;
  yieldLossPct: number | null;
  offBomValue: number;
  unconsumedBomValue: number;
  unpricedLineCount: number;
  assumedFloatPct: number;
  scrapPct: number;
  worst: { woNo: string; product: string; varianceValue: number }[];
}

export function YieldVarianceCard({ ym }: { ym: string }) {
  const [r, setR] = useState<YieldReport | null>(null);

  useEffect(() => {
    let live = true;
    void api<YieldReport>(`/api/close/yield?ym=${encodeURIComponent(ym)}`).then((res) => {
      if (live && res.ok) setR(res.body as YieldReport);
    });
    return () => {
      live = false;
    };
  }, [ym]);

  if (!r) return null;

  const title = (
    <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
      سنجش ضایعات مواد (اطلاعاتی)
      {r.basis === 'measured' ? (
        <Chip tone="info">اندازه‌گیری‌شده</Chip>
      ) : r.basis === 'insufficient' ? (
        <Chip tone="warn">نمونهٔ کم</Chip>
      ) : (
        <Chip tone="neutral">بدون داده تولید</Chip>
      )}
    </span>
  );

  return (
    <Card title={title}>
      {/* D6 guardrail — make the non-double-count rule impossible to miss. */}
      <div
        style={{
          borderInlineStart: '4px solid var(--amber)',
          background: 'var(--amber-bg)',
          borderRadius: 'var(--radius-xs)',
          padding: '8px 12px',
          lineHeight: 1.9,
          marginBottom: 12,
        }}
      >
        این عدد فقط <b>اطلاعاتی</b> است. مطابق سیاست D6، ضایعات به «بهای تمام‌شده» اضافه نمی‌شود —
        «پوشش ضایعات» (درآمد ضایعات، حدود {formatPercent(r.scrapPct)} فروش) از پیش آن را جذب می‌کند.
        این را به بها اضافه <b>نکنید</b> و از پوشش کم <b>نکنید</b>؛ فقط برای بررسی «آیا BOM واقعی
        است و آیا دسته‌ای بیش‌مصرف دارد».
      </div>

      {r.basis === 'no_production_data' ? (
        <p style={{ color: 'var(--ink-3)', margin: 0 }}>
          دستور ساختِ تکمیل‌شده‌ای برای این ماه ثبت نشده — بدون داده تولید، ضایعات قابل اندازه‌گیری
          نیست.
        </p>
      ) : (
        <>
          <div className="kpi-grid" style={{ marginBottom: 12 }}>
            <Kpi
              label="ضایعات اندازه‌گیری‌شده"
              value={r.yieldLossPct == null ? '—' : formatPercent(r.yieldLossPct)}
              tone={r.yieldLossPct != null && r.yieldLossPct > 0 ? 'bad' : 'good'}
            />
            <Kpi label="فرض شناور (ماه)" value={formatPercent(r.assumedFloatPct)} />
            <Kpi
              label="مصرف واقعی − BOM"
              value={formatRial(r.varianceValue)}
              tone={r.varianceValue > 0 ? 'bad' : 'good'}
            />
            <Kpi label="تعداد دستور ساخت" value={r.sampleWos.toLocaleString('fa-IR')} />
          </div>

          {r.basis === 'insufficient' ? (
            <p style={{ color: 'var(--amber)', marginTop: 0 }}>
              نمونهٔ کم (کمتر از ۳ دستور ساخت) — این درصد یک نشانه است، نه یک روند؛ با احتیاط
              بخوانید.
            </p>
          ) : null}

          {/* Coverage flags — a big off-BOM/unconsumed/unpriced share means the headline is incomplete. */}
          {r.offBomValue || r.unconsumedBomValue || r.unpricedLineCount ? (
            <p style={{ color: 'var(--ink-3)', lineHeight: 1.9 }}>
              {r.offBomValue ? <>مصرف خارج از BOM: {formatRial(r.offBomValue)} · </> : null}
              {r.unconsumedBomValue ? (
                <>مواد BOM بدون مصرف: {formatRial(r.unconsumedBomValue)} · </>
              ) : null}
              {r.unpricedLineCount ? (
                <>مواد بدون قیمت (کنارگذاشته): {r.unpricedLineCount.toLocaleString('fa-IR')}</>
              ) : null}
            </p>
          ) : null}

          {r.worst.length ? (
            <table className="table" style={{ marginTop: 6 }}>
              <thead>
                <tr>
                  <th>دستور ساخت</th>
                  <th>محصول</th>
                  <th>انحراف مصرف</th>
                </tr>
              </thead>
              <tbody>
                {r.worst.map((w) => (
                  <tr key={w.woNo}>
                    <td className="num">{w.woNo}</td>
                    <td>{w.product}</td>
                    <td style={{ color: w.varianceValue > 0 ? 'var(--red)' : 'var(--green)' }}>
                      {formatRial(w.varianceValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      )}
    </Card>
  );
}
